/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

class ParseUtils {
  static regex = /\{@P[^}]*\s*:\s*[^}]*\}/g;
  static subRegex = /:'[a-zA-Z0-9!@#$&()\\-`.+,/\s"]*'/;
  static keyMap: { [key: string]: string } = {};

  /**
   * @example
   * const localeKey = "Hi {@P .name:'test'}"
   * const result = ParseUtils.formatLocaleKey(localeKey)
   * output:
   * "Hi {@P .name}"
   * @param localeKey
   * @returns string with only prop name
   */
  static formatLocaleKey(localeKey: string) {
    let startIdx = 0;
    let output = '';
    while (startIdx <= localeKey.length) {
      const placeHolderIdx = localeKey.indexOf('{@P ', startIdx);
      if (placeHolderIdx === -1) {
        output += localeKey.substring(startIdx, localeKey.length);
        break;
      }
      output += localeKey.substring(startIdx, placeHolderIdx);
      const closeBrIdx = localeKey.indexOf('}', startIdx);
      const dynamicProp = localeKey.substring(placeHolderIdx, closeBrIdx + 1);
      const [key, value] = this.getKeyAndValue(dynamicProp);
      this.addKeyToMap(key, value);
      output += `${dynamicProp.split(':')[0]}}`;
      startIdx = closeBrIdx + 1;
    }
    return output;
  }

  static getKeyAndValue(match: string) {
    const colonIndex = match.indexOf(':');
    const endBracketIdx = match.indexOf('}');
    return [`${match.substring(0, colonIndex)}}`, match.substring(colonIndex + 1, endBracketIdx)];
  }

  static addKeyToMap(key: string, value: string) {
    this.keyMap[key] = value;
  }

  /**
   * @example
   * const localeKey = "Ola {@P .name}"
   * const result = ParseUtils.replaceWithValue(localeKey)
   * output:
   * "Ola test"
   * @param localizedValue
   * @returns string by replacing the propName with actual value
   */
  static replaceWithValue(localizedValue: string) {
    let startIdx = 0;
    let output = '';
    while (startIdx <= localizedValue.length) {
      const placeHolderIdx = localizedValue.indexOf('{@P ', startIdx);
      if (placeHolderIdx === -1) {
        output += localizedValue.substring(startIdx, localizedValue.length);
        break;
      }
      output += localizedValue.substring(startIdx, placeHolderIdx);
      const closeBrIdx = localizedValue.indexOf('}', startIdx);
      const dynamicProp = localizedValue.substring(placeHolderIdx, closeBrIdx + 1);
      output += this.keyMap[dynamicProp];
      startIdx = closeBrIdx + 1;
    }
    return output;
  }
}

export default ParseUtils;
